/**
 * Mix & match bundle picker — fill N slots from a product grid, then open GoKwik checkout.
 */
class MixMatchBundle extends HTMLElement {
  #selections = [];
  #activeSlot = 0;
  #qvImages = [];
  #qvIndex = 0;
  #qvCard = null;
  #qvVariants = [];
  #onQvKeydown = null;
  #qvDrag = null;
  #onQvPointerMove = null;
  #onQvPointerUp = null;
  #currentPage = 1;
  #pageSize = 20;

  connectedCallback() {
    this.picksRequired = Number(this.dataset.picksRequired) || 3;
    this.allowDuplicates = this.dataset.allowDuplicates === 'true';
    this.showVariants = this.dataset.showVariants === 'true';
    this.parentVariantId = this.dataset.parentVariantId;
    this.sectionId = this.dataset.sectionId || '';
    this.#selections = Array(this.picksRequired).fill(null);
    this.#activeSlot = 0;

    this.slotsEl = this.querySelector('[data-mm-slots]');
    this.catalogEl = this.querySelector('[data-mm-catalog]');
    this.gridEl = this.querySelector('[data-mm-grid]');
    this.paginationEl = this.querySelector('[data-mm-pagination]');
    this.footerEl = this.querySelector('[data-mm-footer]');
    this.hintEl = this.querySelector('[data-mm-hint]');
    this.countBadgeEl = this.querySelector('[data-mm-count-badge]');
    this.statusEl = this.querySelector('[data-mm-status]');
    this.errorEl = this.querySelector('[data-mm-error]');
    this.buyBtn = this.querySelector('[data-mm-buy]');
    this.countEl = this.querySelector('[data-mm-count]');
    this.sortSelect = this.querySelector('[data-mm-sort]');
    this.qvEl = this.querySelector('[data-mm-qv]');
    this.qvGalleryEl = this.querySelector('[data-mm-qv-gallery]');
    this.qvTrackEl = this.querySelector('[data-mm-qv-track]');
    this.qvDotsEl = this.querySelector('[data-mm-qv-dots]');
    this.qvBadgeEl = this.querySelector('[data-mm-qv-badge]');
    this.qvTitleEl = this.querySelector('[data-mm-qv-title]');
    this.qvPriceEl = this.querySelector('[data-mm-qv-price]');
    this.qvCompareEl = this.querySelector('[data-mm-qv-compare]');
    this.qvReviewsEl = this.querySelector('[data-mm-qv-reviews]');
    this.qvVariantsEl = this.querySelector('[data-mm-qv-variants]');
    this.qvCustomNameEl = this.querySelector('[data-mm-qv-custom-name]');
    this.qvCustomNameInput = this.querySelector('[data-mm-qv-custom-name-input]');
    this.qvSpecsEl = this.querySelector('[data-mm-qv-specs]');
    this.qvAddBtn = this.querySelector('[data-mm-qv-add]');
    this.qvAddLabelEl = this.querySelector('[data-mm-qv-add-label]');

    this.#pageSize = Number(this.catalogEl?.dataset.pageSize) || 20;

    // Shopify's ?page= would slice the 250-product load; client pagination uses mm_page instead.
    const url = new URL(window.location.href);
    const shopifyPage = Number(url.searchParams.get('page') || '1');
    if (shopifyPage > 1) {
      url.searchParams.delete('page');
      window.location.replace(url.toString());
      return;
    }

    this.#bindEvents();
    if (this.sortSelect?.value) {
      this.#sortGrid(this.sortSelect.value, { resetPage: false });
    }
    const startPage = Number(url.searchParams.get('mm_page') || '1') || 1;
    this.#showPage(startPage);
    this.#render();
  }

  disconnectedCallback() {
    this.#unbindQuickView();
  }

  #bindEvents() {
    this.sortSelect?.addEventListener('change', () => {
      this.#sortGrid(this.sortSelect.value, { resetPage: true });
    });

    this.addEventListener('click', (event) => {
      const pageBtn = event.target.closest('[data-mm-page]');
      if (pageBtn && this.contains(pageBtn)) {
        event.preventDefault();
        if (pageBtn.disabled || pageBtn.classList.contains('is-current')) return;
        const page = Number(pageBtn.dataset.mmPage);
        if (page > 0) this.#showPage(page, { scroll: true });
        return;
      }

      const card = event.target.closest('[data-mm-product]');
      if (!card || !this.contains(card)) return;

      const quickViewTrigger = event.target.closest('[data-mm-quick-view]');
      if (quickViewTrigger) {
        event.preventDefault();
        this.#openQuickView(card);
        return;
      }

      const variantPicker = event.target.closest('[data-mm-variant]');
      if (variantPicker) {
        event.preventDefault();
        this.#setCardVariant(card, variantPicker);
        return;
      }

      const addBtn = event.target.closest('[data-mm-add-product]');
      if (!addBtn) return;

      if (addBtn.classList.contains('is-added')) {
        this.#removeProduct(card.dataset.productId);
        return;
      }

      if (card.classList.contains('is-disabled')) return;

      // Multi-variant or Customized Clutch products must go through quick view.
      const variantCount = Number(card.dataset.variantCount) || 0;
      const needsCustomName = card.dataset.customizedClutch === 'true';
      if (variantCount > 1 || needsCustomName) {
        this.#openQuickView(card);
        return;
      }

      this.#selectProduct(
        card,
        card.dataset.variantId,
        card.dataset.variantTitle || ''
      );
    });

    this.slotsEl?.addEventListener('click', (event) => {
      const removeBtn = event.target.closest('[data-mm-remove]');
      if (removeBtn) {
        const index = Number(removeBtn.dataset.mmRemove);
        this.#clearSlot(index);
        return;
      }

      const slot = event.target.closest('[data-mm-slot]');
      if (slot) {
        this.#activeSlot = Number(slot.dataset.mmSlot);
        this.#render();
      }
    });

    this.qvEl?.addEventListener('click', (event) => {
      if (event.target.closest('[data-mm-qv-close]')) {
        this.#closeQuickView();
        return;
      }

      const addBtn = event.target.closest('[data-mm-qv-add]');
      if (addBtn) {
        this.#addFromQuickView();
        return;
      }

      const variantBtn = event.target.closest('[data-mm-qv-variant]');
      if (variantBtn) {
        this.#selectQvVariant(variantBtn.dataset.variantId);
        return;
      }

      const dot = event.target.closest('[data-mm-qv-dot]');
      if (dot) {
        this.#showQvImage(Number(dot.dataset.mmQvDot));
      }
    });

    this.qvGalleryEl?.addEventListener('pointerdown', (event) => this.#onQvPointerDown(event));

    this.buyBtn?.addEventListener('click', () => this.#submit());
  }

  #getGridItems() {
    return Array.from(this.gridEl?.querySelectorAll('[data-mm-item]') || []);
  }

  #showPage(page, { scroll = false } = {}) {
    const items = this.#getGridItems();
    const pageSize = this.#pageSize > 0 ? this.#pageSize : 20;
    const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
    this.#currentPage = Math.min(Math.max(1, Number(page) || 1), totalPages);

    const start = (this.#currentPage - 1) * pageSize;
    const end = start + pageSize;

    items.forEach((item, index) => {
      item.classList.toggle('is-mm-page-hidden', index < start || index >= end);
    });

    this.#renderPaginationControls(totalPages);

    const historyUrl = new URL(window.location.href);
    historyUrl.searchParams.delete('page');
    historyUrl.searchParams.delete('section_id');
    if (this.#currentPage <= 1) {
      historyUrl.searchParams.delete('mm_page');
    } else {
      historyUrl.searchParams.set('mm_page', String(this.#currentPage));
    }
    window.history.replaceState({}, '', historyUrl.toString());

    if (scroll) {
      this.catalogEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  #renderPaginationControls(totalPages) {
    if (!this.catalogEl) return;

    let nav = this.paginationEl || this.catalogEl.querySelector('[data-mm-pagination]');
    if (totalPages <= 1) {
      if (nav) {
        nav.hidden = true;
        nav.innerHTML = '';
      }
      this.paginationEl = nav;
      return;
    }

    if (!nav) {
      nav = document.createElement('nav');
      nav.className = 'mm-bundle__pagination';
      nav.dataset.mmPagination = '';
      this.catalogEl.appendChild(nav);
    }

    this.paginationEl = nav;
    nav.hidden = false;
    nav.setAttribute('aria-label', this.catalogEl.dataset.paginationLabel || 'Pagination');

    const prevLabel = this.catalogEl.dataset.prevLabel || 'Previous page';
    const nextLabel = this.catalogEl.dataset.nextLabel || 'Next page';
    const parts = [];

    parts.push(
      `<button type="button" class="mm-bundle__page-btn" data-mm-page="${this.#currentPage - 1}" ${
        this.#currentPage <= 1 ? 'disabled' : ''
      } aria-label="${prevLabel}">‹</button>`
    );

    for (let i = 1; i <= totalPages; i += 1) {
      if (i === this.#currentPage) {
        parts.push(`<span class="mm-bundle__page-btn is-current" aria-current="page">${i}</span>`);
      } else {
        parts.push(`<button type="button" class="mm-bundle__page-btn" data-mm-page="${i}">${i}</button>`);
      }
    }

    parts.push(
      `<button type="button" class="mm-bundle__page-btn" data-mm-page="${this.#currentPage + 1}" ${
        this.#currentPage >= totalPages ? 'disabled' : ''
      } aria-label="${nextLabel}">›</button>`
    );

    nav.innerHTML = parts.join('');
  }

  #parseQuickViewData(card) {
    const script = card.querySelector('[data-mm-qv-json]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent || '{}');
    } catch {
      return null;
    }
  }

  #openQuickView(card) {
    if (!this.qvEl) return;
    const data = this.#parseQuickViewData(card);
    if (!data) return;

    this.#qvCard = card;
    this.#qvVariants = Array.isArray(data.variants) ? data.variants : [];
    this.#qvImages = Array.isArray(data.images) ? data.images.filter(Boolean) : [];
    if (!this.#qvImages.length && card.dataset.imageUrl) {
      this.#qvImages = [card.dataset.imageUrl];
    }
    this.#qvIndex = 0;

    if (this.qvTitleEl) this.qvTitleEl.textContent = data.title || '';

    const selectedId = card.dataset.variantId || data.selectedVariantId;
    const selectedVariant =
      this.#qvVariants.find((variant) => String(variant.id) === String(selectedId)) || this.#qvVariants[0] || null;

    this.#setQvPrice(selectedVariant?.price || data.price, selectedVariant?.compareAt ?? data.compareAt);

    if (this.qvBadgeEl) {
      if (data.type) {
        this.qvBadgeEl.hidden = false;
        this.qvBadgeEl.textContent = data.type;
      } else {
        this.qvBadgeEl.hidden = true;
        this.qvBadgeEl.textContent = '';
      }
    }

    if (this.qvReviewsEl) {
      if (data.rating != null || data.ratingCount != null) {
        const rating = data.rating != null ? Number(data.rating).toFixed(1) : '';
        const count = data.ratingCount != null ? `${data.ratingCount} Reviews` : '';
        this.qvReviewsEl.hidden = false;
        this.qvReviewsEl.innerHTML = `
          <span class="mm-bundle__qv-reviews-star" aria-hidden="true">★</span>
          ${rating ? `<strong>${this.#escape(rating)}</strong>` : ''}
          ${count ? `<span>${this.#escape(count)}</span>` : ''}
        `;
      } else {
        this.qvReviewsEl.hidden = true;
        this.qvReviewsEl.innerHTML = '';
      }
    }

    this.#renderQvVariants(data.optionNames, selectedVariant?.id || selectedId);
    this.#syncQvCustomNameField(data.customizedClutch === true || card.dataset.customizedClutch === 'true');

    if (this.qvSpecsEl) {
      const specs = [];
      if (data.material) {
        specs.push({ label: 'Material', value: this.#escape(data.material) });
      }
      if (data.vendor) {
        specs.push({ label: 'Brand', value: this.#escape(data.vendor) });
      }

      this.qvSpecsEl.innerHTML = specs
        .map(
          (spec) => `
            <div class="mm-bundle__qv-spec">
              <p class="mm-bundle__qv-spec-label">${spec.label}</p>
              <div class="mm-bundle__qv-spec-value">${spec.value}</div>
            </div>
          `
        )
        .join('');
      this.qvSpecsEl.hidden = specs.length === 0;
    }

    this.#renderQvSlides();
    this.#renderQvDots();
    this.#syncQvAddButton();

    this.qvEl.hidden = false;
    document.body.classList.add('mm-qv-open');
    requestAnimationFrame(() => {
      this.#showQvImage(0, { animate: false });
      if (selectedVariant?.image) {
        this.#focusQvImage(selectedVariant.image);
      }
    });

    this.#onQvKeydown = (event) => {
      if (event.key === 'Escape') this.#closeQuickView();
      if (event.key === 'ArrowRight') this.#showQvImage(this.#qvIndex + 1);
      if (event.key === 'ArrowLeft') this.#showQvImage(this.#qvIndex - 1);
    };
    document.addEventListener('keydown', this.#onQvKeydown);
    this.qvEl.querySelector('.mm-bundle__qv-dialog')?.focus();

    if (this.qvCustomNameEl && !this.qvCustomNameEl.hidden && this.qvCustomNameInput) {
      requestAnimationFrame(() => this.qvCustomNameInput.focus());
    }
  }

  #syncQvCustomNameField(show) {
    if (!this.qvCustomNameEl) return;

    this.qvCustomNameEl.hidden = !show;
    if (this.qvCustomNameInput) {
      this.qvCustomNameInput.value = '';
      this.qvCustomNameInput.classList.remove('is-invalid');
      this.qvCustomNameInput.required = Boolean(show);
    }
  }

  #getQvCustomizedName() {
    if (!this.qvCustomNameEl || this.qvCustomNameEl.hidden || !this.qvCustomNameInput) return '';
    return this.qvCustomNameInput.value.trim();
  }

  #syncQvAddButton() {
    if (!this.qvAddBtn || !this.#qvCard) return;

    const productId = this.#qvCard.dataset.productId;
    const selected = this.#selections.some((item) => item && item.productId === productId);
    const boxFull = this.#isComplete && !selected;

    this.qvAddBtn.classList.toggle('is-added', selected);
    this.qvAddBtn.disabled = boxFull;
    if (this.qvAddLabelEl) {
      this.qvAddLabelEl.textContent = selected
        ? this.dataset.labelQvRemove || this.dataset.labelAdded || 'Remove'
        : this.dataset.labelQvAdd || this.dataset.labelAdd || 'Add';
    }
  }

  #addFromQuickView() {
    if (!this.#qvCard) return;

    const card = this.#qvCard;
    const productId = card.dataset.productId;
    const alreadySelected = this.#selections.some((item) => item && item.productId === productId);

    if (alreadySelected) {
      this.#removeProduct(productId);
      this.#syncQvAddButton();
      return;
    }

    if (card.classList.contains('is-disabled') || this.#isComplete) {
      this.#showError(this.dataset.errorFull || 'Your box is full. Remove an item to change selection.');
      return;
    }

    const needsCustomName =
      card.dataset.customizedClutch === 'true' ||
      (this.qvCustomNameEl && !this.qvCustomNameEl.hidden);
    const customizedName = this.#getQvCustomizedName();

    if (needsCustomName && !customizedName) {
      this.qvCustomNameInput?.classList.add('is-invalid');
      this.qvCustomNameInput?.focus();
      this.#showError(this.dataset.errorCustomName || 'Please enter a customized name for this clutch.');
      return;
    }

    this.qvCustomNameInput?.classList.remove('is-invalid');
    this.#selectProduct(card, card.dataset.variantId, card.dataset.variantTitle || '', customizedName);
    this.#closeQuickView();
  }

  #setQvPrice(price, compareAt) {
    if (this.qvPriceEl) this.qvPriceEl.textContent = price || '';
    if (this.qvCompareEl) {
      if (compareAt) {
        this.qvCompareEl.hidden = false;
        this.qvCompareEl.textContent = compareAt;
      } else {
        this.qvCompareEl.hidden = true;
        this.qvCompareEl.textContent = '';
      }
    }
  }

  #renderQvVariants(optionNames, selectedId) {
    if (!this.qvVariantsEl) return;

    const selectable = this.#qvVariants.filter(
      (variant) => variant && variant.title && variant.title !== 'Default Title'
    );

    if (selectable.length <= 1) {
      this.qvVariantsEl.hidden = true;
      this.qvVariantsEl.innerHTML = '';
      return;
    }

    const label =
      Array.isArray(optionNames) && optionNames.length
        ? optionNames.join(' / ')
        : 'Options';

    this.qvVariantsEl.hidden = false;
    this.qvVariantsEl.innerHTML = `
      <p class="mm-bundle__qv-variants-label">${this.#escape(label)}</p>
      <div class="mm-bundle__qv-variants-list">
        ${selectable
          .map((variant) => {
            const active = String(variant.id) === String(selectedId);
            return `
              <button
                type="button"
                class="mm-bundle__qv-variant${active ? ' is-active' : ''}"
                data-mm-qv-variant
                data-variant-id="${this.#escape(variant.id)}"
                aria-pressed="${active ? 'true' : 'false'}"
              >
                ${this.#escape(variant.title)}
              </button>
            `;
          })
          .join('')}
      </div>
    `;
  }

  #selectQvVariant(variantId) {
    if (!this.#qvCard || !variantId) return;

    const variant = this.#qvVariants.find((item) => String(item.id) === String(variantId));
    if (!variant) return;

    this.#applyVariantToCard(this.#qvCard, {
      id: variant.id,
      title: variant.title || '',
      imageUrl: variant.imageCard || variant.image || '',
    });

    const productId = this.#qvCard.dataset.productId;
    const selectedIndex = this.#selections.findIndex((item) => item && item.productId === productId);
    if (selectedIndex >= 0) {
      const current = this.#selections[selectedIndex];
      this.#selections[selectedIndex] = {
        ...current,
        variantId: String(variant.id),
        variantTitle: variant.title || '',
        image: variant.imageCard || variant.image || current.image || '',
      };
      this.#render();
    }

    this.#setQvPrice(variant.price, variant.compareAt);

    this.qvVariantsEl?.querySelectorAll('[data-mm-qv-variant]').forEach((btn) => {
      const active = String(btn.dataset.variantId) === String(variant.id);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    if (variant.image) {
      this.#focusQvImage(variant.image);
    }

    this.#syncQvAddButton();
  }

  #applyVariantToCard(card, variant) {
    card.dataset.variantId = String(variant.id);
    card.dataset.variantTitle = variant.title || '';

    if (variant.imageUrl) {
      card.dataset.imageUrl = variant.imageUrl;
      const img = card.querySelector('.mm-bundle__product-image');
      if (img) img.src = variant.imageUrl;
    }

    const matchBtn = Array.from(card.querySelectorAll('[data-mm-variant]')).find(
      (btn) => String(btn.dataset.variantId) === String(variant.id)
    );

    if (matchBtn) {
      card.querySelectorAll('[data-mm-variant]').forEach((btn) => {
        btn.classList.toggle('is-active', btn === matchBtn);
      });
    }
  }

  #imageKey(url) {
    if (!url) return '';
    try {
      const path = new URL(url, window.location.origin).pathname;
      return path.replace(/_\d+x(\.[a-z]+)$/i, '$1');
    } catch {
      return String(url).split('?')[0];
    }
  }

  #focusQvImage(imageUrl) {
    if (!imageUrl || !this.qvTrackEl) return;
    const key = this.#imageKey(imageUrl);
    let index = this.#qvImages.findIndex((src) => this.#imageKey(src) === key);

    if (index < 0) {
      this.#qvImages = [imageUrl, ...this.#qvImages];
      this.#renderQvSlides();
      this.#renderQvDots();
      index = 0;
    }

    this.#showQvImage(index);
  }

  #renderQvSlides() {
    if (!this.qvTrackEl) return;
    const title = this.qvTitleEl?.textContent || '';
    this.qvTrackEl.innerHTML = this.#qvImages
      .map(
        (src, index) => `
          <div class="mm-bundle__qv-slide" data-mm-qv-slide="${index}">
            <img class="mm-bundle__qv-image" src="${this.#escape(src)}" alt="${this.#escape(title)}" draggable="false" loading="${index === 0 ? 'eager' : 'lazy'}">
          </div>
        `
      )
      .join('');
    this.qvTrackEl.style.transform = 'translate3d(0, 0, 0)';
  }

  #renderQvDots() {
    if (!this.qvDotsEl) return;
    if (this.#qvImages.length <= 1) {
      this.qvDotsEl.hidden = true;
      this.qvDotsEl.innerHTML = '';
      return;
    }

    this.qvDotsEl.hidden = false;
    this.qvDotsEl.innerHTML = this.#qvImages
      .map(
        (_, index) =>
          `<button type="button" class="mm-bundle__qv-dot${index === 0 ? ' is-active' : ''}" data-mm-qv-dot="${index}" aria-label="Image ${index + 1}"></button>`
      )
      .join('');
  }

  #showQvImage(index, { animate = true } = {}) {
    if (!this.#qvImages.length || !this.qvTrackEl || !this.qvGalleryEl) return;
    const next = ((index % this.#qvImages.length) + this.#qvImages.length) % this.#qvImages.length;
    this.#qvIndex = next;
    this.qvTrackEl.classList.toggle('is-dragging', !animate);
    const width = this.qvGalleryEl.getBoundingClientRect().width || 0;
    this.qvTrackEl.style.transform = `translate3d(${-next * width}px, 0, 0)`;
    this.qvDotsEl?.querySelectorAll('[data-mm-qv-dot]').forEach((dot) => {
      dot.classList.toggle('is-active', Number(dot.dataset.mmQvDot) === next);
    });
  }

  #onQvPointerDown(event) {
    if (!this.qvGalleryEl || !this.qvTrackEl || this.#qvImages.length <= 1) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.#qvDrag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      deltaX: 0,
      locked: false,
      width: this.qvGalleryEl.getBoundingClientRect().width || 1,
    };

    this.qvGalleryEl.classList.add('is-dragging');
    this.qvTrackEl.classList.add('is-dragging');

    this.#onQvPointerMove = (moveEvent) => this.#onQvPointerMoveHandler(moveEvent);
    this.#onQvPointerUp = (upEvent) => this.#onQvPointerUpHandler(upEvent);
    document.addEventListener('pointermove', this.#onQvPointerMove);
    document.addEventListener('pointerup', this.#onQvPointerUp);
    document.addEventListener('pointercancel', this.#onQvPointerUp);
  }

  #onQvPointerMoveHandler(event) {
    if (!this.#qvDrag || event.pointerId !== this.#qvDrag.pointerId || !this.qvTrackEl) return;

    const deltaX = event.clientX - this.#qvDrag.startX;
    const deltaY = event.clientY - this.#qvDrag.startY;

    if (!this.#qvDrag.locked) {
      if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 8) {
        this.#endQvDrag();
        return;
      }
      if (Math.abs(deltaX) > 8) {
        this.#qvDrag.locked = true;
      } else {
        return;
      }
    }

    event.preventDefault();
    this.#qvDrag.deltaX = deltaX;
    const offset = -this.#qvIndex * this.#qvDrag.width + deltaX;
    this.qvTrackEl.style.transform = `translate3d(${offset}px, 0, 0)`;
  }

  #onQvPointerUpHandler(event) {
    if (!this.#qvDrag || event.pointerId !== this.#qvDrag.pointerId) return;
    const threshold = Math.min(80, this.#qvDrag.width * 0.2);
    const deltaX = this.#qvDrag.deltaX;
    this.#endQvDrag();

    if (Math.abs(deltaX) > threshold) {
      this.#showQvImage(this.#qvIndex + (deltaX < 0 ? 1 : -1));
    } else {
      this.#showQvImage(this.#qvIndex);
    }
  }

  #endQvDrag() {
    this.qvGalleryEl?.classList.remove('is-dragging');
    this.qvTrackEl?.classList.remove('is-dragging');
    this.#qvDrag = null;

    if (this.#onQvPointerMove) {
      document.removeEventListener('pointermove', this.#onQvPointerMove);
      this.#onQvPointerMove = null;
    }
    if (this.#onQvPointerUp) {
      document.removeEventListener('pointerup', this.#onQvPointerUp);
      document.removeEventListener('pointercancel', this.#onQvPointerUp);
      this.#onQvPointerUp = null;
    }
  }

  #closeQuickView() {
    if (!this.qvEl || this.qvEl.hidden) return;
    this.#endQvDrag();
    this.qvEl.hidden = true;
    document.body.classList.remove('mm-qv-open');
    this.#qvCard = null;
    this.#qvVariants = [];
    this.#unbindQuickView();
  }

  #unbindQuickView() {
    this.#endQvDrag();
    if (this.#onQvKeydown) {
      document.removeEventListener('keydown', this.#onQvKeydown);
      this.#onQvKeydown = null;
    }
  }

  #sortGrid(sortValue, { resetPage = true } = {}) {
    if (!this.gridEl) return;

    const items = Array.from(this.gridEl.querySelectorAll('[data-mm-item]'));
    if (!items.length) return;

    items.sort((a, b) => {
      const priceA = Number(a.dataset.price) || 0;
      const priceB = Number(b.dataset.price) || 0;
      const titleA = a.dataset.title || '';
      const titleB = b.dataset.title || '';

      switch (sortValue) {
        case 'price-asc':
          return priceA - priceB;
        case 'title-asc':
          return titleA.localeCompare(titleB);
        case 'title-desc':
          return titleB.localeCompare(titleA);
        case 'price-desc':
        default:
          return priceB - priceA;
      }
    });

    const fragment = document.createDocumentFragment();
    items.forEach((item) => fragment.appendChild(item));
    this.gridEl.appendChild(fragment);

    this.#showPage(resetPage ? 1 : this.#currentPage);
  }

  #setCardVariant(card, variantBtn) {
    this.#applyVariantToCard(card, {
      id: variantBtn.dataset.variantId,
      title: variantBtn.dataset.variantTitle || '',
      imageUrl: variantBtn.dataset.imageUrl || '',
    });
  }

  #selectProduct(card, variantId, variantTitle, customizedName = '') {
    if (!variantId) return;

    const productId = card.dataset.productId;
    if (!this.allowDuplicates && this.#selections.some((item) => item && item.productId === productId)) {
      this.#showError(this.dataset.errorDuplicate || 'This product is already in your box.');
      return;
    }

    let slotIndex = this.#activeSlot;
    if (this.#selections[slotIndex] != null) {
      slotIndex = this.#selections.findIndex((item) => item == null);
    }
    if (slotIndex === -1) {
      this.#showError(this.dataset.errorFull || 'Your box is full. Remove an item to change selection.');
      return;
    }

    this.#selections[slotIndex] = {
      productId,
      variantId,
      title: card.dataset.productTitle,
      variantTitle: variantTitle || '',
      customizedName: customizedName || '',
      image:
        card.querySelector('.mm-bundle__product-image')?.currentSrc ||
        card.querySelector('.mm-bundle__product-image')?.src ||
        card.dataset.imageUrl ||
        '',
      imageAlt: card.dataset.productTitle,
    };

    const nextEmpty = this.#selections.findIndex((item) => item == null);
    this.#activeSlot = nextEmpty === -1 ? slotIndex : nextEmpty;
    this.#hideError();
    this.#render();
  }

  #clearSlot(index) {
    this.#selections[index] = null;
    this.#activeSlot = index;
    this.#hideError();
    this.#render();
  }

  #removeProduct(productId) {
    if (!productId) return;
    const index = this.#selections.findIndex((item) => item && item.productId === productId);
    if (index === -1) return;
    this.#clearSlot(index);
  }

  get #filledCount() {
    return this.#selections.filter(Boolean).length;
  }

  get #isComplete() {
    return this.#filledCount === this.picksRequired;
  }

  #render() {
    this.#renderSlots();
    this.#renderGridState();
    this.#renderFooter();
    this.#renderHint();
  }

  #renderHint() {
    if (!this.hintEl) return;
    this.hintEl.hidden = this.#filledCount > 0;
  }

  #renderSlots() {
    if (!this.slotsEl) return;

    this.slotsEl.querySelectorAll('[data-mm-slot]').forEach((slotEl, index) => {
      const selection = this.#selections[index];
      const isActive = index === this.#activeSlot;
      slotEl.classList.toggle('is-filled', Boolean(selection));
      slotEl.classList.toggle('is-active', isActive && !this.#isComplete);

      const mainBtn = slotEl.querySelector('.mm-bundle__slot-main');
      const media = slotEl.querySelector('[data-mm-slot-media]');
      const label = slotEl.querySelector('[data-mm-slot-label]');
      const removeBtn = slotEl.querySelector('[data-mm-remove]');

      if (mainBtn) {
        mainBtn.setAttribute('aria-pressed', isActive && !this.#isComplete ? 'true' : 'false');
      }

      if (selection) {
        if (media) {
          media.innerHTML = selection.image
            ? `<img class="mm-bundle__slot-image" src="${selection.image}" alt="${this.#escape(selection.imageAlt)}" loading="lazy">`
            : `<span class="mm-bundle__slot-number">${index + 1}</span>`;
        }
        if (label) {
          label.textContent = selection.title;
        }
        if (removeBtn) {
          removeBtn.hidden = false;
        }
      } else {
        if (media) {
          media.innerHTML = `<span class="mm-bundle__slot-number">${index + 1}</span>`;
        }
        if (label) {
          label.textContent = this.dataset.slotEmptyLabel || 'Select';
        }
        if (removeBtn) {
          removeBtn.hidden = true;
        }
      }
    });
  }

  #renderGridState() {
    if (!this.gridEl) return;

    this.gridEl.querySelectorAll('[data-mm-product]').forEach((card) => {
      const productId = card.dataset.productId;
      const selected = this.#selections.some((item) => item && item.productId === productId);
      const boxFull = this.#isComplete;
      const blocked = !selected && boxFull;
      card.classList.toggle('is-selected', selected);
      card.classList.toggle('is-disabled', blocked);

      const addBtn = card.querySelector('[data-mm-add-product]');

      if (addBtn) {
        addBtn.classList.toggle('is-added', selected);
        addBtn.setAttribute('aria-pressed', selected ? 'true' : 'false');
      }
    });
  }

  #renderFooter() {
    const remaining = this.picksRequired - this.#filledCount;
    if (this.countEl) {
      this.countEl.textContent = String(this.#filledCount);
    }
    if (this.countBadgeEl) {
      this.countBadgeEl.textContent = String(this.#filledCount);
    }
    if (this.statusEl) {
      if (this.#isComplete) {
        this.statusEl.textContent = this.dataset.statusComplete || 'Your box is ready';
      } else {
        const template = this.dataset.statusRemaining || 'Select __COUNT__ more';
        this.statusEl.textContent = template
          .replaceAll('__COUNT__', String(remaining))
          .replaceAll('{{ count }}', String(remaining));
      }
    }

    if (this.buyBtn) {
      this.buyBtn.disabled = !this.#isComplete;
      this.buyBtn.setAttribute('aria-disabled', this.#isComplete ? 'false' : 'true');
    }
  }

  #buildProperties() {
    const properties = {
      _bundle_type: 'mix_match',
    };

    this.#selections.forEach((item, index) => {
      if (!item) return;
      const n = index + 1;
      const hasVariant = item.variantTitle && item.variantTitle !== 'Default Title';
      let label = hasVariant ? `${item.title} — ${item.variantTitle}` : item.title;
      if (item.customizedName) {
        label = `${item.title} / ${item.customizedName}`;
      }
      properties[`Item ${n}`] = label;
    });

    return properties;
  }

  #buildLineItem() {
    return {
      id: Number(this.parentVariantId),
      quantity: 1,
      properties: this.#buildProperties(),
    };
  }

  #openGokwikCheckout() {
    const lineItem = this.#buildLineItem();
    const w = /** @type {any} */ (window);

    if (typeof w.setBuyNowCheckoutActive === 'function') {
      w.setBuyNowCheckoutActive(true);
    }

    if (typeof w.onBuyNowClick === 'function') {
      w.onBuyNowClick(this.buyBtn, false, [lineItem]);
      return true;
    }

    if (typeof w.onBuyNow === 'function' && this.buyBtn) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(this.parentVariantId));
      window.history.replaceState({}, '', url);
      w.onBuyNow(this.buyBtn);
      return true;
    }

    return false;
  }

  async #submit() {
    if (!this.#isComplete || !this.parentVariantId || !this.buyBtn) return;

    this.buyBtn.disabled = true;
    this.buyBtn.classList.add('is-loading');
    this.#hideError();

    try {
      const opened = this.#openGokwikCheckout();
      if (opened) return;

      this.#showError(this.dataset.errorGokwik || 'GoKwik checkout is unavailable. Please try again.');
    } catch (error) {
      console.error(error);
      this.#showError(this.dataset.errorGokwik || 'Could not open GoKwik checkout. Please try again.');
    } finally {
      this.buyBtn.classList.remove('is-loading');
      this.#renderFooter();
    }
  }

  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.hidden = false;
    this.errorEl.textContent = message;
  }

  #hideError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  #escape(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
  }
}

if (!customElements.get('mix-match-bundle')) {
  customElements.define('mix-match-bundle', MixMatchBundle);
}
